import { ComponentNames } from "lib/constant";
import { fillGrowSlots } from "./fill-grow-slots";
import { fillHpSlots } from "./fill-hp-slots";
import { fillLineupSlots } from "./fill-lineup-slots";
import { fillPlanSlots } from "./fill-plan-slots";

/**
 * Blok type → the props transform that fills its NODE slots.
 *
 * `ORGANISM_MAP` maps a blok type to the real DDS organism and nothing else. Four
 * organisms expose a slot that takes a React node — `zip_code` on the two carousels,
 * `location_slot` on the HP tray, `zipSlot` on the Channel Lineup — and CMS JSON cannot carry a
 * node. These functions
 * supply it, and the canvases call them on the way in.
 *
 * ⚠ EVERY FILLER IS TYPE-ONLY ABOUT ITS ORGANISM. None of them imports the organism as
 * a value, and none may start to: both canvases import this module statically, so a
 * value import would make the organism statically reachable from every v3 page and
 * collapse the `NextDynamic` split. `slot-fillers.spec.ts` asserts it from source,
 * because a rendered tree cannot witness an import specifier.
 *
 * ⚠ ABSENCE IS THE FAILURE MODE, AND IT IS SILENT. A type with no entry renders
 * perfectly with an empty slot: no throw, no log, `ORGANISM_MAP` parity still passes,
 * a smoke test still passes. The only symptom is a missing ZIP and a reserved height
 * (`INTRINSIC_HEIGHT_PX`) that no longer matches. That is why the spec pins the EXACT
 * key set with `toEqual` rather than `toContain`, and why it probes the wiring through
 * both canvases rather than trusting this table alone.
 */
export const SLOT_FILLERS: Record<string, (props: any) => Record<string, unknown>> = {
  [ComponentNames.DDS_GROW_CARD_CAROUSEL]: fillGrowSlots,
  [ComponentNames.DDS_PLAN_CARD_CAROUSEL]: fillPlanSlots,
  [ComponentNames.DDS_HP_TAB_CAROUSEL]: fillHpSlots,
  [ComponentNames.DDS_CHANNEL_LINEUP]: fillLineupSlots
};

/**
 * The props an organism should receive: filled when its type has a filler, untouched
 * when it does not.
 *
 * ⚠ THE `?.` IS LOAD-BEARING. Most mapped organisms have no slot and no entry, and the
 * canvas specs render probe types that have neither — an unguarded
 * `SLOT_FILLERS[type](props)` is a `TypeError` for every one of them, in tests and in
 * production alike.
 */
export function withFilledSlots(type: string, props: Record<string, unknown>): Record<string, unknown> {
  return SLOT_FILLERS[type]?.(props) ?? props;
}
